import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { SafeResourceUrl } from '@angular/platform-browser';
import { User, UserService } from '../../core/user/user.service';
import { PageService } from '../../core/page.service';
import { SubscriptionLayout, SubscriptionService } from '../../core/subscription/subscription.service';
import { formatSecondsAsMinutes } from '../../core/helpers/utils';
import { SubscriptionInterval, SubscriptionPlanName, SubscriptionStatus, SubscriptionDetails, WebsiteTextingSubscription, WebsiteTextingUsage, WebsiteTextingStatus, ProviderPlan } from '../../core/subscription/subscription';
import { SubscriptionPlansCheckoutComponent } from '../../shared/subscription/subscription-plans-checkout/subscription-plans-checkout.component';
import { ChoosePlanSectionComponent } from '../../shared/choose-plan-section/choose-plan-section.component';
import { WebsiteTextingSubscriptionService } from '../../core/subscription/website-texting-subscription.service';
import { AnalyticsPage } from '../../core/internal-analytics.service';
import { AgentService, SmsCapabilitiesStatus } from '../../core/agent/agent.service';
import { ChurnBusterService } from '../../core/churn-buster/churn-buster.service';
import { ActivatedRoute, Router } from '@angular/router';
import { SessionService } from '../../core/session.service';
import { Subscription, interval, takeWhile } from 'rxjs';
import { environment } from '../../../environments/environment';
@Component({
  selector: 'rosie-billing',
  templateUrl: './billing.component.html',
  styleUrls: ['./billing.component.scss'],
  standalone: false
})
export class BillingComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('wtSubscribeModalRef') wtSubscribeModalRef: any;
  @ViewChild('wtCancelModalRef') wtCancelModalRef: any;
  @ViewChild('pauseModalRef') pauseModalRef: any;
  @ViewChild('checkoutRef') checkoutRef: SubscriptionPlansCheckoutComponent;
  @ViewChild('choosePlanRef') choosePlanRef: ChoosePlanSectionComponent;

  SubscriptionInterval = SubscriptionInterval;
  errorMessage = '';
  billingPortalUrl: SafeResourceUrl;
  subscriptionDetails: SubscriptionDetails | null = null;
  isConfirmingCharge = false;
  wtSubscription: WebsiteTextingSubscription | null = null;
  wtUsage: WebsiteTextingUsage | null = null;
  smsStatus: string = null;
  wtLoaded = false;
  providerPlans: ProviderPlan[] = [];
  currentProviderPlanKey = '';
  isChangingProviderPlan = false;
  // Lowercase plan key whose Shopify provider-billing change is in flight (drives
  // the plan button spinner/lock). Null when idle.
  changingProviderPlanKey: string | null = null;
  // True once a Shopify resubscribe attempt comes back "store disconnected" —
  // the merchant uninstalled the app (the only way to cancel on Shopify), so we
  // can't mint a charge and they must reinstall from the App Store.
  shopifyReinstallRequired = false;
  private subscriptions = new Subscription();
  private smsStatusPollingSub: Subscription;
  private readonly SMS_POLLING_INTERVAL_MS = 30 * 1000;

  constructor(
    private user: UserService,
    private page: PageService,
    public subscription: SubscriptionService,
    private agent: AgentService,
    private router: Router,
    private route: ActivatedRoute,
    private churnBuster: ChurnBusterService,
    private session: SessionService,
    private wtSubscriptionService: WebsiteTextingSubscriptionService
  ) {
    this.page.initializePage(`Rosie - Billing`, AnalyticsPage.AccountBillingPage);
  }

  ngOnInit(): void {
    this.subscriptions.add(this.route.data.subscribe(data => this.subscriptionDetails = data['subscriptionDetails']));
    this.loadSubscriptionDetails();
    this.subscriptions.add(this.session.refreshState.subscribe(() => {
      this.loadSubscriptionDetails();
      this.loadWtStatus();
    }));
    this.subscriptions.add(this.subscription.created.subscribe(() => this.loadSubscriptionDetails()));

    const preloaded = this.wtSubscriptionService.currentSubscription;
    if (preloaded) {
      this.wtSubscription = preloaded;
      this.wtLoaded = true;
    }
    this.loadWtStatus();
    this.checkChargeConfirmation();

    if (this.isExternalBillingProvider) {
      this.loadProviderPlans();
    }
  }

  private loadSubscriptionDetails(): void {
    const subscriptionId = this.user.userData?.subscription?.id;
    if (!subscriptionId) {
      return;
    }
    this.subscription.getSubscriptionDetails(subscriptionId).subscribe((details) => this.subscriptionDetails = details);
  }

  get isCanceledAtEndOfBillingCycle(): boolean {
    return this.subscription?.isCanceledAtEndOfBillingCycle;
  }

  get isPaused(): boolean {
    return this.subscription?.isPaused;
  }

  get isInCancelState(): boolean {
    return this.subscription?.isCanceled || this.subscription?.isCanceledAtEndOfBillingCycle;
  }

  get isCanceled(): boolean {
    return this.subscription?.isCanceled;
  }

  get userData(): User {
    return this.user.userData;
  }

  get planInterval(): string {
    return this.subscription.initialInterval === SubscriptionInterval.Yearly ? 'annually' : 'monthly';
  }

  get hasCreditCardOnFile(): boolean {
    return this.user.hasCreditCardOnFile;
  }

  get isTestMode(): boolean {
    return this.user.isTestMode;
  }

  get isTestModeForBothBillingVersions(): boolean {
    return this.user.isTestMode || (!this.user.hasCreditCardOnFile && this.isUserOnTrialBasedBilling);
  }

  get isPaymentMethodInvalid() {
    return this.subscription.isPaymentMethodInvalid;
  }

  get planName() {
    const trialPlanName = this.user.isUserOn7DayFreeTrialVersion ? `${this.subscription.subscriptionData?.trialDays}-day Trial` : 'Trial';
    return this.subscription.isTrialing ? trialPlanName : this.subscription.subscriptionData?.planName;
  }

  get isSubscriptionPastDue() {
    return this.subscription.subscriptionData?.status === SubscriptionStatus.PastDue;
  }

  get minutesIncluded(): number {
    return this.subscription.subscriptionData?.minutesIncluded;
  }

  get hasBillingUrl(): boolean {
    return !!this.subscription.subscriptionData?.billingUrl;
  }

  get isExternalBillingProvider(): boolean {
    return this.subscription.isExternalBillingProvider;
  }

  get isAppleBillingProvider(): boolean {
    return this.subscription.isAppleBillingProvider;
  }

  get isShopifyBillingProvider(): boolean {
    return this.subscription.isShopifyBillingProvider;
  }

  get isPendingApproval(): boolean {
    return this.subscription.isPendingApproval;
  }

  get canceledProviderMessage(): string {
    if (this.subscription.isAppleBillingProvider) {
      return 'ACCOUNT.BILLING.APPLE_CANCELED_MESSAGE';
    }
    if (this.subscription.isShopifyBillingProvider) {
      return 'ACCOUNT.BILLING.SHOPIFY_CANCELED_MESSAGE';
    }
    return 'ACCOUNT.BILLING.PROVIDER_CANCELED_MESSAGE';
  }

  get providerManagedMessage(): string {
    if (this.subscription.isAppleBillingProvider) {
      return 'ACCOUNT.BILLING.APPLE_MANAGED';
    }
    return 'ACCOUNT.BILLING.PROVIDER_MANAGED';
  }

  get providerConfirmationUrl(): string {
    return this.subscription.providerConfirmationUrl;
  }

  get planPrice(): number {
    return this.subscription.subscriptionData?.planCost;
  }

  get planPriceFormatted(): string {
    const planCost = this.planPrice;
    return planCost ? planCost.toLocaleString() : '0';
  }

  get planPriceInterval(): string {
    return this.planInterval === 'annually' ? 'year' : 'month';
  }

  get planRenewalDate(): string {
    return this.subscription.subscriptionData?.billingDate;
  }

  get isTrialing(): boolean {
    // Check both status (new versions) and plan name (older versions) for backward compatibility
    return this.subscription.isTrialing || this.planName === 'Trial';
  }

  get minutesUsedFormatted(): string | number {
    return this.isOverages ? this.subscription.subscriptionData?.minutesIncluded : formatSecondsAsMinutes(this.subscription?.subscriptionData?.secondsUsed)?.split(':')?.[0];
  }

  get availableMinutes(): number {
    const trialMinutes = this.user?.subscription?.trialMinutes || 50;
    return this.user.subscription?.status === SubscriptionStatus.Trialing ? trialMinutes : this.subscription?.subscriptionData?.minutesIncluded;
  }

  get minutesRemaining(): number {
    const minutesRemainingInTrial = Math.floor(this.agent.primary?.trialSecondsRemaining / 60);
    const minutesRemainingOnPlan = this.availableMinutes - Math.floor(this.subscription?.subscriptionData?.secondsUsed / 60);

    return this.isTrialing ? minutesRemainingInTrial : minutesRemainingOnPlan;
  }

  get minutesUsed() {
    return Math.floor(this.subscription?.subscriptionData?.secondsUsed / 60);
  }

  get totalNumCallsBillingPeriod(): number {
    return this.subscription?.subscriptionData?.totalNumCallsBillingPeriod || 0;
  }

  get isOverages(): boolean {
    return (this.subscription.subscriptionData?.secondsUsed / 60) > this.minutesIncluded;
  }

  get initialPlan() {
    return this.subscription.initialPlan;
  }

  get isMobileVersion(): boolean {
    return this.session.isMobileVersion;
  }

  get isProfessionalPlanSelected(): boolean {
    return this.initialPlan === SubscriptionPlanName.Professional;
  }

  get upcomingPlan() {
    return this.subscription.subscriptionData?.upcomingPlanName;
  }

  get isEnterprisePlanSelected(): boolean {
    return this.subscription.isEnterprisePlanSelected
  }

  get hasPaymentFailedTriggered(): boolean {
    return this.subscription.hasPaymentFailedTriggered;
  }


  get subscriptionActionTranslationKey(): string {
    const oldBillingVersionKey = !this.isUserOnFreeMinutesBillingVersion && !this.isUserOn7DayFreeTrialVersion && this.isProfessionalPlanSelected ? 'ACCOUNT.BILLING.UPGRADE_PLAN' : 'ACCOUNT.BILLING.CHANGE_PLAN';
    const isFreeTrialVersion = this.user.isUserOn7DayFreeTrialVersion;
    const newBillingVersionKey = this.hasCreditCardOnFile ? 'ACCOUNT.BILLING.CHANGE_PLAN' : (!isFreeTrialVersion ? 'ACCOUNT.BILLING.ACTIVATE_FREE_TRIAL' : 'ACCOUNT.BILLING.ADD_CREDIT_CARD');
    return this.user.isUserOnFreeMinutesBillingVersion || isFreeTrialVersion ? newBillingVersionKey : oldBillingVersionKey;
  }

  get isUserOnFreeMinutesBillingVersion(): boolean {
    return this.user.isUserOnFreeMinutesBillingVersion;
  }

  get isUserOn7DayFreeTrialVersion(): boolean {
    return this.user.isUserOn7DayFreeTrialVersion;
  }

  get isUserOnBillingV4(): boolean {
    return this.user.isUserOnBillingV4;
  }

  get isUserOnTrialBasedBilling(): boolean {
    return this.user.isUserOnTrialBasedBilling;
  }

  get isWtEnabled(): boolean {
    return this.wtSubscription?.status === WebsiteTextingStatus.Enabled;
  }

  get isWtSetToCancel(): boolean {
    return this.wtSubscription?.status === WebsiteTextingStatus.SetToCancel;
  }

  get isWtInProgress(): boolean {
    return this.smsStatus === 'in_progress';
  }

  get isWtDisabled(): boolean {
    if (this.isWtInProgress) return false;
    return !this.wtSubscription || this.wtSubscription?.status === WebsiteTextingStatus.Disabled;
  }

  // TODO: Remove mock flag — for dev preview only
  mockUsage = false;

  get callMinutesUsed(): number {
    if (this.mockUsage) return 342;
    return Math.floor((this.subscription?.subscriptionData?.secondsUsed || 0) / 60);
  }

  get callMinutesIncluded(): number {
    if (this.mockUsage) return 1000;
    return this.subscription?.subscriptionData?.minutesIncluded || 0;
  }

  get minutesUsedPercent(): number {
    if (!this.callMinutesIncluded) return 0;
    return Math.min((this.callMinutesUsed / this.callMinutesIncluded) * 100, 100);
  }

  get wtConversationsUsed(): number {
    if (this.mockUsage) return 18;
    return this.wtUsage?.usedConversations || 0;
  }

  get wtConversationsIncluded(): number {
    if (this.mockUsage) return 50;
    return this.wtUsage?.includedConversations || 0;
  }

  get wtOverageConversations(): number {
    if (this.mockUsage) return 3;
    return this.wtUsage?.overageConversations || 0;
  }

  get wtIncludedPercent(): number {
    if (!this.wtConversationsIncluded) return 0;
    return Math.min((this.wtConversationsUsed / this.wtConversationsIncluded) * 100, 100);
  }

  get wtOveragePercent(): number {
    const total = this.wtConversationsIncluded + this.wtOverageConversations;
    if (!total) return 0;
    return (this.wtOverageConversations / total) * 100;
  }

  openWtSubscribeModal(): void {
    if (this.subscription.isAppleBillingProvider) {
      return;
    }

    if (this.subscription.isShopifyBillingProvider) {
      // Shopify billing is managed by Shopify — open subscribe modal directly
      this.wtSubscribeModalRef?.open();
      return;
    }

    if (this.hasCreditCardOnFile) {
      this.wtSubscribeModalRef?.open();
    } else {
      this.subscription.open.next({ plan: SubscriptionPlanName.Scale, websiteTextingPreSelected: true });
    }
  }

  openWtCancelModal(): void {
    if (this.subscription.isAppleBillingProvider) {
      return;
    }

    this.wtCancelModalRef?.open();
  }

  reactivateWt(): void {
    if (this.subscription.isAppleBillingProvider) {
      return;
    }

    this.wtSubscriptionService.reactivate().subscribe({
      next: (res) => {
        if (res?.confirmationUrl) {
          // Shopify requires merchant approval — redirect to Shopify
          window.open(res.confirmationUrl, '_top');
          return;
        }
        this.wtSubscription = res;
      }
    });
  }

  onWtSubscribed(): void {
    this.loadWtStatus();
  }

  onWtCancelled(): void {
    this.loadWtStatus();
  }

  private loadWtStatus(): void {
    this.wtSubscriptionService.getStatus().subscribe({
      next: (sub) => {
        this.wtSubscription = sub;
        this.wtLoaded = true;
      }
    });
    this.wtSubscriptionService.getUsage().subscribe({
      next: (usage) => this.wtUsage = usage
    });
    this.loadSmsCapabilitiesStatus();
  }

  private loadSmsCapabilitiesStatus(): void {
    this.agent.getSmsCapabilitiesStatus().subscribe({
      next: (response: SmsCapabilitiesStatus) => {
        this.smsStatus = response?.status || 'inactive';
        if (this.smsStatus === 'in_progress') {
          this.startSmsPolling();
        }
      }
    });
  }

  private startSmsPolling(): void {
    this.stopSmsPolling();
    this.smsStatusPollingSub = interval(this.SMS_POLLING_INTERVAL_MS).pipe(
      takeWhile(() => this.smsStatus === 'in_progress')
    ).subscribe(() => {
      this.agent.getSmsCapabilitiesStatus().subscribe({
        next: (response: SmsCapabilitiesStatus) => {
          const newStatus = response?.status || 'inactive';
          if (newStatus !== 'in_progress') {
            this.smsStatus = newStatus;
            this.stopSmsPolling();
            this.loadWtStatus();
          }
        }
      });
    });
  }

  private stopSmsPolling(): void {
    if (this.smsStatusPollingSub) {
      this.smsStatusPollingSub.unsubscribe();
      this.smsStatusPollingSub = null;
    }
  }

  ngAfterViewInit(): void {
    if (this.subscription.isExternalBillingProvider) {
      return; // ChurnBuster only applies to Stripe
    }

    if (this.churnBuster.isChurnBusterEnabled() && this.subscription.subscriptionData?.externalSubscriptionId) {
      this.churnBuster.initializeCancelFlow();
    }
  }

  redirectToBillingPortal(): void {
    this.subscription.redirectToBillingPortal();
  }

  refreshPaymentState() {
    this.subscription.refreshPaymentState().subscribe();
  }

  subscribe() {
    if (this.isEnterprisePlanSelected) {
      return;
    }
    this.subscription.open.next(null);
  }

  getSelectedPlan() {
    return this.subscription.plans?.find((plan) => this.subscription.subscriptionData?.planName === plan.name);
  }

  get selectedInterval(): SubscriptionInterval {
    return this.subscription.selectedInterval;
  }

  selectInterval(interval: SubscriptionInterval): void {
    this.subscription.selectedInterval = interval;
    if (this.checkoutRef) {
      this.checkoutRef.selectInterval(interval);
    }
    // The no-card trial state renders the plan picker through the shared
    // choose-plan section (ENG-2292); its checkout follows the header toggle.
    this.choosePlanRef?.selectInterval(interval);
  }

  // Lowercase plan key the API recommends from the onboarding intent.
  get recommendedPlan(): string {
    return this.user.recommendedPlan;
  }

  openProviderConfirmation(): void {
    if (this.providerConfirmationUrl) {
      window.open(this.providerConfirmationUrl, '_top');
    }
  }

  private checkChargeConfirmation(): void {
    this.subscriptions.add(this.route.queryParams.subscribe(params => {
      const chargeId = params['charge_id'];

      if (!chargeId || !this.isExternalBillingProvider) {
        return;
      }

      this.isConfirmingCharge = true;
      this.subscription.confirmProviderCharge(chargeId).subscribe({
        next: () => {
          this.isConfirmingCharge = false;
          this.user.getUser().subscribe();
          this.loadSubscriptionDetails();
          this.loadProviderPlans();
          this.loadWtStatus();
          this.router.navigate([], {
            queryParams: { charge_id: null },
            queryParamsHandling: 'merge',
            replaceUrl: true
          });
        },
        error: () => {
          this.isConfirmingCharge = false;
          this.router.navigate([], {
            queryParams: { charge_id: null },
            queryParamsHandling: 'merge',
            replaceUrl: true
          });
        }
      });
    }));
  }

  private loadProviderPlans(): void {
    this.subscription.getProviderPlans().subscribe({
      next: (plans) => {
        this.providerPlans = plans;
        this.currentProviderPlanKey = this.subscription.subscriptionData?.planName?.toLowerCase() || '';
      }
    });
  }

  onProviderPlanSelected(planKey: string): void {
    if (this.changingProviderPlanKey) {
      return;
    }
    this.isChangingProviderPlan = true;
    this.changingProviderPlanKey = planKey;

    // When opened inside the native app's billing webview, mobileReturn is a
    // deep link the post-approval confirm redirect uses to bounce back into the
    // app (instead of the embedded Shopify admin).
    const mobileReturn = this.route.snapshot.queryParams['mobileReturn'] || undefined;

    // Only the retry path mints a brand-new charge (which REPLACES any live
    // Shopify subscription). Guard against a stale local pending_approval status:
    // if the provider says the subscription is already active, treat this as a
    // plan change, never a replacement. Otherwise a bogus reconfirm state could
    // route a plan-card click into the destructive replace flow.
    const needsCharge = this.isPendingApproval && !this.subscription.isProviderActive;

    const request$ = needsCharge
      ? this.subscription.retryProviderBilling(planKey, mobileReturn)
      : this.subscription.changeProviderPlan(planKey, mobileReturn);

    request$.subscribe({
      next: (result) => {
        if (result?.confirmationUrl) {
          // Page navigates to Shopify — keep the button locked/spinning.
          window.open(result.confirmationUrl, '_top');
          return;
        }
        this.isChangingProviderPlan = false;
        this.changingProviderPlanKey = null;
      },
      error: () => {
        this.isChangingProviderPlan = false;
        this.changingProviderPlanKey = null;
      }
    });
  }

  isUnpausing = false;

  openPauseModal(): void {
    this.pauseModalRef?.open();
  }

  onPaused(): void {
    this.loadSubscriptionDetails();
    this.loadWtStatus();
  }

  unpauseSubscription(): void {
    const subscriptionId = this.user.userData?.subscription?.id;
    if (!subscriptionId || this.isUnpausing) return;

    this.isUnpausing = true;
    this.subscription.unpauseSubscription(subscriptionId).subscribe({
      next: () => {
        this.isUnpausing = false;
        this.loadSubscriptionDetails();
      },
      error: () => {
        this.isUnpausing = false;
      }
    });
  }

  cancelSubscription() {
    if (this.subscription.isExternalBillingProvider) {
      return; // External providers manage cancellation through their own platform
    }

    if (this.churnBuster.isChurnBusterEnabled()) {
      console.log('Churn Buster cancel flow initiated');
    } else {
      window.open(`${this.subscription.subscriptionData?.billingUrl}/subscriptions/${this.subscription.subscriptionData?.externalSubscriptionId}/cancel`, '_blank');
    }
  }

  goToSetup() {
    this.router.navigate(['/settings', { section: 'launch' }]);
  }

  resubscribe() {
    if (this.subscription.isExternalBillingProvider) {
      return;
    }
    window.open(`${this.subscription.subscriptionData?.billingUrl}/subscriptions/${this.subscription.subscriptionData?.externalSubscriptionId}/reactivate`, '_blank');
  }

  // ENG-2057: Apple/Shopify subscriptions can't be resubscribed from the admin —
  // send the user to the place where they actually can.
  openRosieApp(): void {
    window.open('https://apps.apple.com/us/app/rosie-ai-business-receptionist/id6757593086', '_blank');
  }

  goToShopifyResubscribe(): void {
    // Resubscribing on Shopify means approving a NEW app charge (the app stays
    // installed — no reinstall). retryProviderBilling mints that charge and
    // returns Shopify's approval URL, which IS the resubscribe page; opening it
    // sends the merchant straight there, then Shopify redirects back to admin.
    this.shopifyReinstallRequired = false;
    // Pass the merchant's plan only when it's a real plan key. planName on a
    // canceled sub is the STATUS ("Canceled"), and sending that 404'd the retry.
    // With no key the backend reuses the stored provider plan, then the default.
    const knownPlanKey = this.providerPlans.find((plan) => plan.key === this.currentProviderPlanKey)?.key;
    this.subscription.retryProviderBilling(knownPlanKey).subscribe({
      next: (result) => {
        if (result?.confirmationUrl) {
          window.open(result.confirmationUrl, '_top');
          return;
        }
        // Fallback: drop them on their Shopify apps list to reopen Rosie.
        const base = environment.shopifyAdminUrl;
        const handle = (this.user.activeBusiness?.commerceShopDomain || '')
          .replace(/^https?:\/\//, '')
          .replace(/\.myshopify\.com.*$/, '')
          .replace(/\/.*$/, '');
        window.open(handle ? `${base}/store/${handle}/apps` : base, '_blank');
      },
      // Store disconnected (app uninstalled — the only way to cancel on Shopify)
      // returns 409 shop_disconnected / reinstall_required: we can't mint a charge,
      // so surface the reason and offer a reinstall path to the App Store instead.
      error: (err) => {
        const body = err?.error ?? {};
        const data = body.data ?? {};
        if (body.error === 'shop_disconnected' || data.reinstallRequired || data.reinstall_required) {
          this.shopifyReinstallRequired = true;
        }
      },
    });
  }

  // No active Shopify store to charge — send the merchant to the App Store
  // listing to reinstall the Rosie app and start a fresh subscription.
  reinstallFromAppStore(): void {
    window.open('https://apps.shopify.com/rosie-ai', '_blank');
  }

  private setBrowserTabTitle(): void {
    this.page.initializePage(`Rosie - Settings`);
  }

  getCouponDisplayText(coupon: any): string {
    if (!coupon) return '';

    // Determine discount amount
    let discountText = '';
    if (coupon.percentOff) {
      discountText = `${coupon.percentOff}% off`;
    } else if (coupon.amountOff) {
      discountText = `$${coupon.amountOff} off`;
    }

    // Add duration information
    if (coupon.duration === 'repeating' && coupon.durationInMonths) {
      return `${discountText} for ${coupon.durationInMonths} ${coupon.durationInMonths === 1 ? 'month' : 'months'}`;
    } else if (coupon.duration === 'once') {
      return `${discountText} for 1 month`;
    } else if (coupon.duration === 'forever') {
      return `${discountText} forever`;
    }

    // Fallback
    return discountText;
  }

  ngOnDestroy(): void {
    this.stopSmsPolling();
    this.subscriptions.unsubscribe();
  }
}
